/** packages/shared-components/src/components/KeyboardLayoutSelector/keyboardLayoutData.ts */

import { LAYOUT_CHANGEKEY_PALETTE_104_KEYCODES } from "../Keyboard/keyboardLayouts";
import type { AllKeyboardType } from "@eweadn-qiankun/shared-types";
import {
  EXTRA_KEYS_MATRIX,
  EXTRA_KEYS_MATRIX_BEIYING,
  EXTRA_KEYS_MATRIX_HUAFENDA,
  EXTRA_KEYS_MATRIX_ZHWX,
  EXTRA_KEYS_MATRIX_RONGYUAN,
  EXTRA_KEYS_MATRIX_SDCX,
  DEFAULT_KEYBOARD_MACRO_SLOT_COUNT,
  resolveEffectiveMacroSlotCount,
  resolveMacroKeyCodeMatrix,
  type MacroSlotDeviceContext,
} from "@eweadn-qiankun/shared-utils";
// i18next-extract-mark-ns-start keyboard-layout-selector-dynamic
// Dynamic keys used in this file:
// t("keyboard.keyCategory.basic")
// t("keyboard.keyCategory.media")
// t("keyboard.keyCategory.tool")
// t("keyboard.keyCategory.light")
// t("keyboard.keyCategory.control")
// t("keyboard.keyCategory.special")
// t("keyboard.keyCategory.mouse")
// t("keyboard.keyCategory.macro")
// i18next-extract-mark-ns-end keyboard-layout-selector-dynamic
/**
 * 按键布局配置
 * 用于 KeyboardLayoutSelector 组件
 * ✅ 只定义 KeyCode 矩阵，宽度和间距复用现有配置
 */

/** 分类配置 */
export interface KeyLayoutCategory {
  id: string;
  label: string; // 保留用于兼容
  labelKey: string; // i18n key
  /** KeyCode 矩阵 */
  keyCodeMatrix: number[][];
  /** 键盘类型（决定宽度和间距） */
  keyboardType?: AllKeyboardType;
}

/** Gamma75：特殊键（穿透 / Fn1 / Fn2） */
const GAMMA75_SPECIAL_CATEGORY: KeyLayoutCategory = {
  id: "special",
  label: "特殊",
  labelKey: "keyboard.keyCategory.special",
  keyCodeMatrix: EXTRA_KEYS_MATRIX.special,
  keyboardType: "special",
};

const KEY_LAYOUT_CATEGORIES_DEFAULT: KeyLayoutCategory[] = [
  {
    id: "104",
    label: "基础按键",
    labelKey: "keyboard.keyCategory.basic",
    keyCodeMatrix: LAYOUT_CHANGEKEY_PALETTE_104_KEYCODES,
    keyboardType: "keymap104",
  },
  {
    id: "media",
    label: "多媒体",
    labelKey: "keyboard.keyCategory.media",
    keyCodeMatrix: EXTRA_KEYS_MATRIX["media"],
    keyboardType: "media",
  },
  // { id: 'system', label: '系统', keyCodeMatrix: systemKeysMatrix },
  {
    id: "light",
    label: "灯效",
    labelKey: "keyboard.keyCategory.light",
    keyCodeMatrix: EXTRA_KEYS_MATRIX["light"],
    keyboardType: "light",
  },
  {
    id: "control",
    label: "控制",
    labelKey: "keyboard.keyCategory.control",
    keyCodeMatrix: EXTRA_KEYS_MATRIX["control"],
    keyboardType: "control",
  },
  // { id: 'triMode', label: '三模', keyCodeMatrix: triModeKeysMatrix },
  {
    id: "mouse",
    label: "鼠标",
    labelKey: "keyboard.keyCategory.mouse",
    keyCodeMatrix: EXTRA_KEYS_MATRIX["mouse"],
    keyboardType: "mouse",
  },
  {
    id: "macro",
    label: "快捷指令键",
    labelKey: "keyboard.keyCategory.macro",
    keyCodeMatrix: EXTRA_KEYS_MATRIX["macro"],
    keyboardType: "macro",
  },
];

function withGamma75SpecialCategory(
  categories: KeyLayoutCategory[],
  keyboardType: string
): KeyLayoutCategory[] {
  if (keyboardType !== "gamma75") return categories;
  const mouseIdx = categories.findIndex((c) => c.id === "mouse");
  const insertAt = mouseIdx >= 0 ? mouseIdx : categories.length;
  const next = categories.slice();
  next.splice(insertAt, 0, GAMMA75_SPECIAL_CATEGORY);
  return next;
}

const KEY_LAYOUT_CATEGORIES_RONGYUAN: KeyLayoutCategory[] = [
  {
    id: "104",
    label: "基础按键",
    labelKey: "keyboard.keyCategory.basic",
    /** 与其它方案一致：改键源盘用 keymap104，底行原 Fn 位为右 Win（231） */
    keyCodeMatrix: LAYOUT_CHANGEKEY_PALETTE_104_KEYCODES,
    keyboardType: "keymap104",
  },
  {
    id: "media",
    label: "多媒体",
    labelKey: "keyboard.keyCategory.media",
    keyCodeMatrix: EXTRA_KEYS_MATRIX_RONGYUAN["media"],
    keyboardType: "media",
  },
  {
    id: "special",
    label: "特殊",
    labelKey: "keyboard.keyCategory.special",
    keyCodeMatrix: EXTRA_KEYS_MATRIX_RONGYUAN["special"],
    keyboardType: "special",
  },
  {
    id: "mouse",
    label: "鼠标",
    labelKey: "keyboard.keyCategory.mouse",
    keyCodeMatrix: EXTRA_KEYS_MATRIX_RONGYUAN["mouse"],
    keyboardType: "mouse",
  },
  {
    id: "macro",
    label: "快捷指令键",
    labelKey: "keyboard.keyCategory.macro",
    keyCodeMatrix: EXTRA_KEYS_MATRIX_RONGYUAN["macro"],
    keyboardType: "macro",
  },
];

function readMacroSlotContextFromStorage(): MacroSlotDeviceContext {
  const equipmentData = localStorage.getItem("equipmentData");
  if (!equipmentData) return {};
  try {
    const parsed = JSON.parse(equipmentData) as MacroSlotDeviceContext;
    return {
      solutionRovider: parsed.solutionRovider,
      keyboardType: parsed.keyboardType,
      vendorId: parsed.vendorId,
      productId: parsed.productId,
      displayname: parsed.displayname,
    };
  } catch {
    return {};
  }
}

/** 固件槽位少于默认 16 时，改键面板快捷指令键仅展示 M0–Mn（如 Gamma75=6、DK63=8） */
function withMacroSlotLimitForCategories(
  categories: KeyLayoutCategory[],
  ctx: MacroSlotDeviceContext
): KeyLayoutCategory[] {
  const slotCount = resolveEffectiveMacroSlotCount(ctx);
  if (slotCount >= DEFAULT_KEYBOARD_MACRO_SLOT_COUNT) return categories;
  return categories.map((cat) =>
    cat.id === "macro"
      ? { ...cat, keyCodeMatrix: resolveMacroKeyCodeMatrix(ctx) }
      : cat
  );
}

/**
 * 所有分类配置
 * ✅ 基础按键使用改键专用完整 104 矩阵 `LAYOUT_CHANGEKEY_PALETTE_104_KEYCODES`（类型 keymap104）
 */
export const KEY_LAYOUT_CATEGORIES = (): KeyLayoutCategory[] => {
  // 判断方案商，用于显示隐藏改键类型及键码
  const equipmentData: any = localStorage.getItem("equipmentData");
  let solutionRovider: any = "xingshan";
  let keyboardType = "";
  if (equipmentData) {
    solutionRovider = JSON.parse(equipmentData).solutionRovider;
    keyboardType = JSON.parse(equipmentData).keyboardType;
  }
  if (solutionRovider === "beiying") {
    return [
      {
        id: "104",
        label: "基础按键",
        labelKey: "keyboard.keyCategory.basic",
        keyCodeMatrix: LAYOUT_CHANGEKEY_PALETTE_104_KEYCODES,
        keyboardType: "keymap104",
      },
      {
        id: "media",
        label: "多媒体",
        labelKey: "keyboard.keyCategory.media",
        keyCodeMatrix: EXTRA_KEYS_MATRIX_BEIYING["media"],
        keyboardType: "media",
      },
      {
        id: "tool",
        label: "工具",
        labelKey: "keyboard.keyCategory.tool",
        keyCodeMatrix: EXTRA_KEYS_MATRIX_BEIYING["tool"],
        keyboardType: "tool",
      },
      {
        id: "mouse",
        label: "鼠标",
        labelKey: "keyboard.keyCategory.mouse",
        keyCodeMatrix: EXTRA_KEYS_MATRIX_BEIYING["mouse"],
        keyboardType: "mouse",
      },
      {
        id: "macro",
        label: "快捷指令键",
        labelKey: "keyboard.keyCategory.macro",
        keyCodeMatrix: EXTRA_KEYS_MATRIX_BEIYING["macro"],
        keyboardType: "macro",
      },
    ];
  } else if (solutionRovider === "ZHWX") {
    return [
      {
        id: "104",
        label: "基础按键",
        labelKey: "keyboard.keyCategory.basic",
        keyCodeMatrix: LAYOUT_CHANGEKEY_PALETTE_104_KEYCODES,
        keyboardType: "keymap104",
      },
      {
        id: "media",
        label: "多媒体",
        labelKey: "keyboard.keyCategory.media",
        keyCodeMatrix: EXTRA_KEYS_MATRIX_ZHWX["media"],
        keyboardType: "media",
      },
      {
        id: "light",
        label: "灯效",
        labelKey: "keyboard.keyCategory.light",
        keyCodeMatrix: EXTRA_KEYS_MATRIX_ZHWX["light"],
        keyboardType: "light",
      },
      {
        id: "control",
        label: "控制",
        labelKey: "keyboard.keyCategory.control",
        keyCodeMatrix: EXTRA_KEYS_MATRIX_ZHWX["control"],
        keyboardType: "control",
      },
      {
        id: "macro",
        label: "快捷指令键",
        labelKey: "keyboard.keyCategory.macro",
        keyCodeMatrix: EXTRA_KEYS_MATRIX_ZHWX["macro"],
        keyboardType: "macro",
      },
    ];
  } else if (solutionRovider === "huafenda") {
    if (["75Pro", "M98", "75ProTFT"].includes(keyboardType)) {
      return [
        {
          id: "104",
          label: "基础按键",
          labelKey: "keyboard.keyCategory.basic",
          keyCodeMatrix: LAYOUT_CHANGEKEY_PALETTE_104_KEYCODES,
          keyboardType: "keymap104",
        },
        {
          id: "media",
          label: "多媒体",
          labelKey: "keyboard.keyCategory.media",
          keyCodeMatrix: EXTRA_KEYS_MATRIX_HUAFENDA["media"],
          keyboardType: "media",
        },
        {
          id: "mouse",
          label: "鼠标",
          labelKey: "keyboard.keyCategory.mouse",
          keyCodeMatrix: EXTRA_KEYS_MATRIX_HUAFENDA["mouse"],
          keyboardType: "mouse",
        },
        {
          id: "macro",
          label: "快捷指令键",
          labelKey: "keyboard.keyCategory.macro",
          keyCodeMatrix: EXTRA_KEYS_MATRIX_HUAFENDA["macro"],
          keyboardType: "macro",
        },
      ];
    }
    return [
      {
        id: "104",
        label: "基础按键",
        labelKey: "keyboard.keyCategory.basic",
        keyCodeMatrix: LAYOUT_CHANGEKEY_PALETTE_104_KEYCODES,
        keyboardType: "keymap104",
      },
      {
        id: "media",
        label: "多媒体",
        labelKey: "keyboard.keyCategory.media",
        keyCodeMatrix: EXTRA_KEYS_MATRIX_HUAFENDA["media"],
        keyboardType: "media",
      },
      {
        id: "light",
        label: "灯效",
        labelKey: "keyboard.keyCategory.light",
        keyCodeMatrix: EXTRA_KEYS_MATRIX_HUAFENDA["light"],
        keyboardType: "light",
      },
      {
        id: "control",
        label: "控制",
        labelKey: "keyboard.keyCategory.control",
        keyCodeMatrix: EXTRA_KEYS_MATRIX_HUAFENDA["control"],
        keyboardType: "control",
      },
      {
        id: "mouse",
        label: "鼠标",
        labelKey: "keyboard.keyCategory.mouse",
        keyCodeMatrix: EXTRA_KEYS_MATRIX_HUAFENDA["mouse"],
        keyboardType: "mouse",
      },
      {
        id: "macro",
        label: "快捷指令键",
        labelKey: "keyboard.keyCategory.macro",
        keyCodeMatrix: EXTRA_KEYS_MATRIX_HUAFENDA["macro"],
        keyboardType: "macro",
      },
    ];
  } else if (solutionRovider === "rongyuan") {
    return KEY_LAYOUT_CATEGORIES_RONGYUAN;
  } else if (solutionRovider === "SDCX") {
    return [
      {
        id: "104",
        label: "基础按键",
        labelKey: "keyboard.keyCategory.basic",
        keyCodeMatrix: LAYOUT_CHANGEKEY_PALETTE_104_KEYCODES,
        keyboardType: "keymap104",
      },
      {
        id: "media",
        label: "多媒体",
        labelKey: "keyboard.keyCategory.media",
        keyCodeMatrix: EXTRA_KEYS_MATRIX_SDCX["media"],
        keyboardType: "media",
      },
      {
        id: "macro",
        label: "快捷指令键",
        labelKey: "keyboard.keyCategory.macro",
        keyCodeMatrix: EXTRA_KEYS_MATRIX_SDCX["macro"],
        keyboardType: "macro",
      },
    ];
  } else {
    const macroSlotCtx = readMacroSlotContextFromStorage();
    return withGamma75SpecialCategory(
      withMacroSlotLimitForCategories(
        KEY_LAYOUT_CATEGORIES_DEFAULT,
        macroSlotCtx
      ),
      keyboardType
    );
  }
};
